import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FadeUp } from "./Reveal";
import { MysteryCountdown } from "./MysteryCountdown";
import { useLang } from "./LanguageContext";

const SHOW_IMG =
  "/stage/show-stretch-lux.webp";

const copy = {
  en: {
    badge: "Live · Tableside",
    kicker: "The Signature Ritual",
    h2a: "The",
    h2b: "Mozzarella",
    h2c: "Show",
    body: "Warm curd drawn, folded and pulled into silken ribbons — an artisan's craft performed at the table and served while still warm. A rare spectacle of skill and provenance, composed for venues that treat hospitality as an art.",
    stats: [
      { v: "100%", l: "Made by hand" },
      { v: "Live", l: "Tableside craft" },
      { v: "1:1", l: "Bespoke to your venue" },
    ],
    cta: "Request a Presentation",
  },
  it: {
    badge: "Live · Al tavolo",
    kicker: "Il Rituale d'Autore",
    h2a: "Il",
    h2b: "Mozzarella",
    h2c: "Show",
    body: "Cagliata calda filata, piegata e tirata in nastri di seta — un mestiere artigiano eseguito al tavolo e servito ancora caldo. Uno spettacolo raro di tecnica e provenienza, composto per sale che trattano l'ospitalità come un'arte.",
    stats: [
      { v: "100%", l: "Fatta a mano" },
      { v: "Live", l: "Al tavolo, dal vivo" },
      { v: "1:1", l: "Su misura per la tua sala" },
    ],
    cta: "Richiedi una Presentazione",
  },
  ru: {
    badge: "Вживую · У стола",
    kicker: "Фирменный ритуал",
    h2a: "The",
    h2b: "Mozzarella",
    h2c: "Show",
    body: "Тёплый сгусток вытягивается и складывается в шёлковые ленты — ремесло мастера прямо у стола, подача ещё тёплой моцареллы. Редкое зрелище мастерства для заведений, где гостеприимство — это искусство.",
    stats: [
      { v: "100%", l: "Ручная работа" },
      { v: "Live", l: "У стола, вживую" },
      { v: "1:1", l: "Под ваше заведение" },
    ],
    cta: "Запросить презентацию",
  },
  es: {
    badge: "En Vivo · Junto a la Mesa",
    kicker: "El Ritual de Autor",
    h2a: "El",
    h2b: "Mozzarella",
    h2c: "Show",
    body: "Cuajada tibia hilada, plegada y estirada en cintas de seda — un oficio artesanal ejecutado junto a la mesa y servido aún tibio. Un espectáculo raro de técnica y origen, compuesto para salones que tratan la hospitalidad como un arte.",
    stats: [
      { v: "100%", l: "Hecha a mano" },
      { v: "Live", l: "Junto a la mesa, en vivo" },
      { v: "1:1", l: "A medida para tu salón" },
    ],
    cta: "Solicitar una Presentación",
  },
};

export const MozzarellaShow = ({ countdown = true }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const { lang } = useLang();
  const t = copy[lang];

  return (
    <section ref={ref} id="mozzarella-show" className="py-24 md:py-32 bg-wine text-bg overflow-hidden" data-testid="mozzarella-show-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6">
          <FadeUp>
            <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden rounded-sm shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)]">
              <motion.img
                src={SHOW_IMG}
                alt="Fresh mozzarella pulled by hand during the live Mozzarella Show"
                style={{ y }}
                className="absolute inset-0 w-full h-[120%] object-cover"
              />
              <div className="absolute top-5 left-5 flex items-center gap-2 bg-wine/70 backdrop-blur-md rounded-full px-4 py-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-terracotta opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-terracotta" />
                </span>
                <span className="font-sans text-[11px] uppercase tracking-[0.25em] text-bg">{t.badge}</span>
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-6 lg:pl-6">
          <FadeUp>
            <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-6">{t.kicker}</p>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.92] tracking-tight">
              {t.h2a} <em className="italic">{t.h2b}</em> {t.h2c}
            </h2>
            <p className="font-sans text-base md:text-lg text-bg/75 max-w-xl mt-8 leading-relaxed">
              {t.body}
            </p>

            <div className="grid grid-cols-3 gap-4 md:gap-6 mt-10 border-t border-bg/15 pt-8 max-w-xl">
              {t.stats.map((s) => (
                <div key={s.l}>
                  <p className="font-serif text-3xl md:text-4xl text-bg leading-none">{s.v}</p>
                  <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-bg/50 mt-2">{s.l}</p>
                </div>
              ))}
            </div>

            {countdown && (
              <div className="mt-10">
                <MysteryCountdown variant="dark" />
              </div>
            )}

            <button
              data-testid="show-reserve-button"
              onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
              className="group relative overflow-hidden rounded-full bg-terracotta px-8 py-4 text-sm font-sans tracking-wide text-white mt-8"
            >
              <span className="relative z-10">{t.cta}</span>
              <span className="absolute inset-0 bg-bg scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <span className="absolute inset-0 z-10 flex items-center justify-center text-wine opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-150 font-sans tracking-wide">
                {t.cta}
              </span>
            </button>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};
